import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, EASE } from "./motion";

const gotoField = (index) => {
  window.dispatchEvent(new CustomEvent("df:field", { detail: index }));
  document.getElementById("arbeitsfelder")?.scrollIntoView({ behavior: "smooth" });
};

export const CasesSection = () => {
  const { t } = useLanguage();
  const cs = t.cases;
  const [open, setOpen] = useState(null);

  return (
    <section data-testid="cases-section" className="bg-mist/40">
      <div className="mx-auto max-w-[1600px] px-6 py-14 sm:px-12 sm:py-[72px] md:px-16">
        <Reveal y={14}>
          <p className="text-[13px] font-medium uppercase tracking-label text-wave">{cs.label}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <h2 className="font-head mt-8 max-w-2xl text-3xl leading-tight text-ink sm:text-5xl">
            {cs.title}
          </h2>
        </Reveal>
        <div className="mt-14 grid items-start gap-x-20 sm:mt-20 sm:grid-cols-2">
          {cs.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={item.title} delay={i * 0.05} className="border-t border-ink/10" data-testid={`case-${i}`}>
                <button
                  data-testid={`case-toggle-${i}`}
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="group flex w-full items-start justify-between gap-6 py-7 text-left sm:py-8"
                >
                  <span className="flex items-baseline gap-5">
                    <span className="font-head text-lg text-wave">{String(i + 1).padStart(2, "0")}</span>
                    <span
                      className={`font-head text-xl leading-snug transition-colors duration-500 sm:text-2xl ${
                        isOpen ? "text-deep" : "text-ink group-hover:text-deep"
                      }`}
                    >
                      {item.title}
                    </span>
                  </span>
                  <Plus
                    size={18}
                    strokeWidth={1.5}
                    aria-hidden="true"
                    className={`mt-1.5 shrink-0 text-wave transition-transform duration-500 ${
                      isOpen ? "rotate-45" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.55, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-md text-base font-light leading-relaxed text-ink/70">
                        {item.text}
                      </p>
                      <button
                        data-testid={`case-cta-${i}`}
                        onClick={() => gotoField(item.field)}
                        className="group/cta mb-8 mt-6 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.22em] text-deep transition-colors hover:text-ink"
                      >
                        {cs.cta}
                        <ArrowRight
                          size={13}
                          strokeWidth={1.5}
                          aria-hidden="true"
                          className="transition-transform duration-500 group-hover/cta:translate-x-1"
                        />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
